import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import {
    X,
    Plus,
    User,
    Briefcase,
    MapPin,
    Building2,
    FileText,
    Clock3,
    Phone,
    MessageSquare,
    AlertCircle,
    Sparkles,
    CircleDot,
} from 'lucide-react'
import styles from './AddNewFollowup.module.css'
import AuthContext from '../../store/auth-context'
import { BASE_URL_API } from '../../References/urls'

const REMINDER_OPTIONS = [
    { label: '30m', value: '0.5' },
    { label: '1h', value: '1' },
    { label: '3h', value: '3' },
    { label: '8h', value: '8' },
    { label: 'Tomorrow', value: '24' },
]

const PRIORITY_OPTIONS = ['low', 'medium', 'high']
const FOLLOWUP_CHANNELS = ['call', 'whatsapp']
const MAX_YEARS_FOR_CUSTOM_DATE = 5
const EMPTY_DEFAULT_VALUES = {}
const MIN_STYLIST_SEARCH_CHARACTERS = 2
const FETCH_SUGGESTED_USERS_DEFAULT_FILTERS = {
    sortBy: '',
    genderType: '',
    maritalStatus: '',
    primaryWorkDomain: '',
    secondaryWorkDomain: '',
    tertiaryWorkDomain: '',
    workDomainAtAnyPreference: false,
    fromDate: '',
    toDate: '',
    minAge: '',
    maxAge: '',
    minSalary: '',
    maxSalary: '',
    desiredLocations: [],
    onboardedVia: '',
}

const toDateTimeLocalValue = (date) => {
    const pad = (value) => String(value).padStart(2, '0')
    const year = date.getFullYear()
    const month = pad(date.getMonth() + 1)
    const day = pad(date.getDate())
    const hour = pad(date.getHours())
    const minute = pad(date.getMinutes())
    return `${year}-${month}-${day}T${hour}:${minute}`
}

const getDefaultCustomDateTime = () => {
    const date = new Date()
    date.setHours(date.getHours() + 1)
    return toDateTimeLocalValue(date)
}

const getHoursDiffFromNow = (dateTimeString) => {
    const targetTime = new Date(dateTimeString).getTime()
    if (Number.isNaN(targetTime)) {
        return null
    }
    const diffInMs = targetTime - Date.now()
    const diffInHours = diffInMs / (1000 * 60 * 60)
    return Number(diffInHours.toFixed(2))
}

const getFirstNonEmptyValue = (...values) => {
    for (const value of values) {
        if (value === null || value === undefined) {
            continue
        }
        if (typeof value === 'string' && value.trim() === '') {
            continue
        }
        return value
    }
    return ''
}

const getCityFromStylistRecord = (record) => {
    const city = getFirstNonEmptyValue(record?.currentCity, record?.city, '')
    if (city) return city
    const rawCurrentLocation = getFirstNonEmptyValue(record?.currentLocation, '')
    if (typeof rawCurrentLocation === 'string' && rawCurrentLocation.includes(',')) {
        const splitLocation = rawCurrentLocation.split(',').map((item) => item.trim()).filter(Boolean)
        if (splitLocation.length >= 2) {
            return splitLocation[splitLocation.length - 3] || splitLocation[splitLocation.length - 2] || splitLocation[0]
        }
    }
    return 'Delhi'
}

const AddNewFollowup = (props) => {
    const {
        isOpen,
        onClose,
        onAdd,
        isSubmitting = false,
        title = 'Add New Follow-up',
        subtitle = 'Create a follow-up in seconds',
        newNote,
        setNewNote,
        defaultValues,
        stylist_identifier,
        stylistData,
        jobIdentifier,
    } = props

    const authCtx = useContext(AuthContext)
    const resolvedDefaultValues = defaultValues || EMPTY_DEFAULT_VALUES
    const [hasHydratedOnOpen, setHasHydratedOnOpen] = useState(false)
    const [stylistSearchInput, setStylistSearchInput] = useState('')
    const [suggestedStylists, setSuggestedStylists] = useState([])
    const [selectedSuggestedStylist, setSelectedSuggestedStylist] = useState(null)
    const [isSearchingStylists, setIsSearchingStylists] = useState(false)

    const prefilledStylistValues = useMemo(() => {
        const fallbackCity = (() => {
            const location = getFirstNonEmptyValue(stylistData?.currentCity, stylistData?.city, '')
            if (location) return location
            const rawCurrentLocation = getFirstNonEmptyValue(stylistData?.currentLocation, '')
            if (typeof rawCurrentLocation === 'string' && rawCurrentLocation.includes(',')) {
                const splitLocation = rawCurrentLocation.split(',').map((item) => item.trim()).filter(Boolean)
                if (splitLocation.length >= 2) {
                    return splitLocation[splitLocation.length - 3] || splitLocation[splitLocation.length - 2] || splitLocation[0]
                }
            }
            return 'Delhi'
        })()

        return {
            name: getFirstNonEmptyValue(stylistData?.stylist_name, ''),
            role: getFirstNonEmptyValue(stylistData?.primaryWorkDomain, stylistData?.role, 'Stylist'),
            city: fallbackCity,
            phone: getFirstNonEmptyValue(stylistData?.contact_number, stylistData?.whatsappNumber, ''),
            stylist_identifier: getFirstNonEmptyValue(stylist_identifier, stylistData?.stylist_id, stylistData?.stylist_identifier, stylistData?.contact_number, ''),
        }
    }, [stylistData, stylist_identifier])

    const initialData = useMemo(() => ({
        name: '',
        role: 'Stylist',
        city: 'Delhi',
        salon: '',
        note: '',
        hours: '1',
        scheduleType: 'quick',
        customDateTime: getDefaultCustomDateTime(),
        priority: 'medium',
        channel: 'call',
        ...prefilledStylistValues,
        ...resolvedDefaultValues,
    }), [resolvedDefaultValues, prefilledStylistValues])

    const isControlled = typeof setNewNote === 'function' && !!newNote
    const [internalForm, setInternalForm] = useState(initialData)
    const [errorMessage, setErrorMessage] = useState('')
    const [isApiSubmitting, setIsApiSubmitting] = useState(false)

    const formData = isControlled ? { ...initialData, ...newNote } : internalForm
    const minCustomDateTime = useMemo(() => toDateTimeLocalValue(new Date()), [isOpen])
    const maxCustomDateTime = useMemo(() => {
        const maxDate = new Date()
        maxDate.setFullYear(maxDate.getFullYear() + MAX_YEARS_FOR_CUSTOM_DATE)
        return toDateTimeLocalValue(maxDate)
    }, [isOpen])

    const updateField = (key, value) => {
        setErrorMessage('')
        if (isControlled) {
            setNewNote((prev) => ({ ...prev, [key]: value }))
            return
        }
        setInternalForm((prev) => ({ ...prev, [key]: value }))
    }

    const updateFields = (nextValues) => {
        setErrorMessage('')
        if (isControlled) {
            setNewNote((prev) => ({ ...prev, ...nextValues }))
            return
        }
        setInternalForm((prev) => ({ ...prev, ...nextValues }))
    }

    const hasProvidedCandidate = useMemo(
        () => Boolean(getFirstNonEmptyValue(
            stylist_identifier,
            stylistData?.stylist_id,
            stylistData?.stylist_identifier,
            resolvedDefaultValues?.stylist_identifier,
            ''
        )),
        [resolvedDefaultValues?.stylist_identifier, stylistData?.stylist_id, stylistData?.stylist_identifier, stylist_identifier]
    )

    const shouldShowStylistSearch = !hasProvidedCandidate
    const shouldShowCandidateDetails = hasProvidedCandidate || Boolean(selectedSuggestedStylist?.data)

    const suggestedStylistOptions = useMemo(() => (
        suggestedStylists.map((item) => {
            const name = getFirstNonEmptyValue(item?.stylist_name, item?.stylist_username, 'Unknown')
            const phone = getFirstNonEmptyValue(item?.contact_number, item?.whatsappNumber, '')
            const city = getCityFromStylistRecord(item)
            return {
                value: getFirstNonEmptyValue(item?.stylist_id, item?.stylist_identifier, item?.contact_number, String(item?.ID || '')),
                label: phone ? `${name} • ${phone} • ${city}` : `${name} • ${city}`,
                data: item,
            }
        })
    ), [suggestedStylists])

    const fetchSuggestedUsers = useCallback(async (searchKey) => {
        const trimmedSearchKey = String(searchKey || '').trim()
        if (trimmedSearchKey.length < MIN_STYLIST_SEARCH_CHARACTERS) {
            setSuggestedStylists([])
            return
        }

        setIsSearchingStylists(true)

        try {
            const fetchSuggestedUsersResponse = await fetch(BASE_URL_API + '/fetchSuggestedUsers', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    key: trimmedSearchKey,
                    ...FETCH_SUGGESTED_USERS_DEFAULT_FILTERS,
                }),
            })

            if (!fetchSuggestedUsersResponse.ok) {
                setSuggestedStylists([])
                return
            }

            const fetchSuggestedUsersRespo = await fetchSuggestedUsersResponse.json()
            const requestSuccessful =
                fetchSuggestedUsersRespo?.status === 'success' ||
                fetchSuggestedUsersRespo?.success === true

            if (requestSuccessful) {
                setSuggestedStylists(Array.isArray(fetchSuggestedUsersRespo?.response) ? fetchSuggestedUsersRespo.response : [])
            } else if (fetchSuggestedUsersRespo?.message === 'token Expired, please login again!') {
                authCtx.logout()
            } else {
                setSuggestedStylists([])
            }
        } catch (error) {
            console.error('fetchSuggestedUsers error:', error)
            setSuggestedStylists([])
        }

        setIsSearchingStylists(false)
    }, [authCtx])

    const handleSelectSuggestedStylist = (selectedOption) => {
        setSelectedSuggestedStylist(selectedOption || null)
        if (!selectedOption?.data) {
            updateField('stylist_identifier', '')
            return
        }

        const selectedStylist = selectedOption.data
        updateFields({
            name: getFirstNonEmptyValue(selectedStylist?.stylist_name, selectedStylist?.stylist_username, formData?.name, ''),
            role: getFirstNonEmptyValue(selectedStylist?.primaryWorkDomain, formData?.role, 'Stylist'),
            city: getCityFromStylistRecord(selectedStylist),
            phone: getFirstNonEmptyValue(selectedStylist?.contact_number, selectedStylist?.whatsappNumber, formData?.phone, ''),
            stylist_identifier: getFirstNonEmptyValue(
                selectedStylist?.stylist_id,
                selectedStylist?.stylist_identifier,
                selectedStylist?.contact_number,
                ''
            ),
        })
    }

    const resetForm = () => {
        if (isControlled) {
            setNewNote((prev) => ({ ...prev, ...initialData, hours: '1', scheduleType: 'quick', customDateTime: getDefaultCustomDateTime() }))
            setStylistSearchInput('')
            setSuggestedStylists([])
            setSelectedSuggestedStylist(null)
            return
        }
        setInternalForm(initialData)
        setStylistSearchInput('')
        setSuggestedStylists([])
        setSelectedSuggestedStylist(null)
    }

    useEffect(() => {
        if (!isOpen) return
        const onEscape = (event) => {
            if (event.key === 'Escape') {
                onClose?.()
            }
        }
        window.addEventListener('keydown', onEscape)
        return () => window.removeEventListener('keydown', onEscape)
    }, [isOpen, onClose])

    useEffect(() => {
        if (!isOpen || isControlled) return
        setInternalForm(initialData)
    }, [isOpen, isControlled, initialData])

    useEffect(() => {
        if (!isOpen) {
            setHasHydratedOnOpen(false)
            setStylistSearchInput('')
            setSuggestedStylists([])
            setSelectedSuggestedStylist(null)
            return
        }
        if (!isControlled || typeof setNewNote !== 'function' || hasHydratedOnOpen) return

        setNewNote((prev) => {
            const previousData = prev || {}
            const sanitizedPreviousData = Object.fromEntries(
                Object.entries(previousData).filter(([, value]) => value !== undefined)
            )

            return {
                ...initialData,
                ...sanitizedPreviousData,
            }
        })
        setHasHydratedOnOpen(true)
    }, [isOpen, isControlled, setNewNote, initialData, hasHydratedOnOpen])

    useEffect(() => {
        if (!isOpen || !shouldShowStylistSearch) return
        const trimmedSearchInput = String(stylistSearchInput || '').trim()
        if (trimmedSearchInput.length < MIN_STYLIST_SEARCH_CHARACTERS) {
            setSuggestedStylists([])
            return
        }

        const debounceTimer = window.setTimeout(() => {
            fetchSuggestedUsers(trimmedSearchInput)
        }, 350)

        return () => window.clearTimeout(debounceTimer)
    }, [fetchSuggestedUsers, isOpen, shouldShowStylistSearch, stylistSearchInput])


    if (!isOpen) return null;

    const quickNameFill = () => {
        const trimmedName = String(formData.name || '').trim()
        if (!trimmedName) {
            setErrorMessage('Please enter candidate name first to auto-fill note.')
            return
        }
        const starterNote = `Follow up with ${trimmedName} for ${formData.role || 'Stylist'} position.`
        updateField('note', starterNote)
    }

    const handleSubmit = async () => {
        if (!String(formData.name || '').trim()) {
            setErrorMessage('Candidate name is required.')
            return
        }

        let computedHours = String(formData.hours || '1')
        let scheduledAt = null

        if (formData.scheduleType === 'custom') {
            const diffInHours = getHoursDiffFromNow(formData.customDateTime)
            if (diffInHours === null) {
                setErrorMessage('Please select a valid date and time.')
                return
            }
            if (diffInHours <= 0) {
                setErrorMessage('Custom follow-up time should be in the future.')
                return
            }
            const selectedDate = new Date(formData.customDateTime)
            const maxDate = new Date()
            maxDate.setFullYear(maxDate.getFullYear() + MAX_YEARS_FOR_CUSTOM_DATE)
            if (selectedDate > maxDate) {
                setErrorMessage(`Custom follow-up date cannot be more than ${MAX_YEARS_FOR_CUSTOM_DATE} years.`)
                return
            }
            computedHours = String(Math.max(0.5, diffInHours))
            // Format datetime as "YYYY-MM-DD HH:mm:ss" for the API
            scheduledAt = formData.customDateTime.replace('T', ' ') + ':00'
        } else {
            // Convert hours offset to absolute datetime
            const target = new Date(Date.now() + Number(computedHours) * 3600 * 1000)
            const pad = (n) => String(n).padStart(2, '0')
            scheduledAt = `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())} ${pad(target.getHours())}:${pad(target.getMinutes())}:00`
        }

        const apiPayload = {
            token: authCtx.token,
            stylist_identifier: getFirstNonEmptyValue(
                formData.stylist_identifier,
                stylist_identifier,
                stylistData?.stylist_id,
                stylistData?.stylist_identifier,
                formData.phone,
                stylistData?.contact_number,
                ''
            ),
            executive_identifier: authCtx.userIdentifier || '',
            source_type: formData.source || 'manual',
            source_ref_id: formData.sourceRefId || '',
            source_label: formData.sourceLabel || '',
            job_id: jobIdentifier || null,
            placement_id: formData.placementId || null,
            salon_identifier: formData.salonIdentifier || '',
            salon_name: formData.salon || '',
            followup_type: formData.channel || 'call',
            scheduled_at: scheduledAt,
            priority: formData.priority || 'medium',
            ai_candidate_score: formData.score || null,
            tags: formData.tags || [],
            note: formData.note || '',
            internal_note: formData.internalNote || '',
            created_by: authCtx.userIdentifier || '',
        }

        setIsApiSubmitting(true)
        setErrorMessage('')

        try {
            const response = await fetch(BASE_URL_API + '/addAFollowUp', {
                method: 'POST',
                body: JSON.stringify(apiPayload),
            })

            if (!response.ok) {
                setErrorMessage('Server error. Please try again.')
                setIsApiSubmitting(false)
                return
            }

            const responseBody = await response.json()

            const isRequestSuccessful =
                responseBody?.status === 'success' ||
                responseBody?.success === true ||
                responseBody?.success === 'true'

            if (isRequestSuccessful) {
                const newFollowup = {
                    ...formData,
                    id:
                        responseBody?.followup_id ||
                        responseBody?.followupId ||
                        responseBody?.response?.id ||
                        responseBody?.response?.followupId ||
                        Date.now(),
                    followupUuid:
                        responseBody?.followup_uuid ||
                        responseBody?.followupUuid ||
                        responseBody?.response?.followup_uuid ||
                        responseBody?.response?.followupUuid ||
                        null,
                    urgencyScore:
                        responseBody?.urgency_score ||
                        responseBody?.urgencyScore ||
                        responseBody?.response?.urgency_score ||
                        responseBody?.response?.urgencyScore ||
                        null,
                    executiveIdentifier: authCtx.userIdentifier,
                    hours: computedHours,
                    scheduledAt: scheduledAt,
                    customScheduleAt: formData.scheduleType === 'custom' ? formData.customDateTime : null,
                }
                if (typeof onAdd === 'function') {
                    onAdd(newFollowup)
                }
                resetForm()
                onClose?.()
            } else if (responseBody?.message === 'token Expired, please login again!') {
                authCtx.logout()
            } else {
                setErrorMessage(responseBody?.message || 'Failed to create follow-up. Please try again.')
            }
        } catch (error) {
            console.error('AddNewFollowup submit error:', error)
            setErrorMessage('Network error. Please check your connection and try again.')
        }

        setIsApiSubmitting(false)
    }

    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget) {
            onClose?.()
        }
    }

    return (
        <div className={styles.mainWrapper} onMouseDown={handleOverlayClick}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <div className={styles.headingWrap}>
                        <div className={styles.iconBubble}><Sparkles size={14} /></div>
                        <div>
                            <h3 className={styles.modalTitle}>{title}</h3>
                            <p className={styles.modalSubtitle}>{subtitle}</p>
                        </div>
                    </div>
                    <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="Close">
                        <X size={14} />
                    </button>
                </div>

                <div className={styles.formGrid}>
                    {shouldShowStylistSearch && (
                        <div className={`${styles.fieldGroup} ${styles.fullWidth}`}>
                            <span className={styles.label}><User size={12} /> Search Stylist</span>
                            <Select
                                className={styles.stylistSearchSelect}
                                classNamePrefix="followup-stylist-search"
                                options={suggestedStylistOptions}
                                value={selectedSuggestedStylist}
                                isClearable
                                isLoading={isSearchingStylists}
                                placeholder={`Type at least ${MIN_STYLIST_SEARCH_CHARACTERS} characters to search stylist`}
                                onChange={handleSelectSuggestedStylist}
                                onInputChange={(inputValue, actionMeta) => {
                                    if (actionMeta.action === 'input-change') {
                                        setStylistSearchInput(inputValue)
                                    }
                                }}
                                noOptionsMessage={() => {
                                    const trimmedSearchInput = String(stylistSearchInput || '').trim()
                                    if (trimmedSearchInput.length < MIN_STYLIST_SEARCH_CHARACTERS) {
                                        return `Type at least ${MIN_STYLIST_SEARCH_CHARACTERS} characters`
                                    }
                                    if (isSearchingStylists) {
                                        return 'Searching stylists...'
                                    }
                                    return 'No stylists found'
                                }}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                            />
                        </div>
                    )}

                    {shouldShowCandidateDetails && (
                        <>
                            <label className={styles.fieldGroup}>
                                <span className={styles.label}><User size={12} /> Candidate Name*</span>
                                <input
                                    value={formData.name || ''}
                                    onChange={(event) => updateField('name', event.target.value)}
                                    placeholder="e.g. Priya Sharma"
                                    autoFocus
                                />
                            </label>

                            <label className={styles.fieldGroup}>
                                <span className={styles.label}><Briefcase size={12} /> Role</span>
                                <input
                                    value={formData.role || ''}
                                    onChange={(event) => updateField('role', event.target.value)}
                                    placeholder="e.g. Senior Stylist"
                                />
                            </label>

                            <label className={styles.fieldGroup}>
                                <span className={styles.label}><MapPin size={12} /> City</span>
                                <input
                                    value={formData.city || ''}
                                    onChange={(event) => updateField('city', event.target.value)}
                                    placeholder="e.g. Delhi"
                                />
                            </label>

                            <label className={styles.fieldGroup}>
                                <span className={styles.label}><Building2 size={12} /> Salon / Company</span>
                                <input
                                    value={formData.salon || ''}
                                    onChange={(event) => updateField('salon', event.target.value)}
                                    placeholder="e.g. Naturals CP"
                                />
                            </label>
                        </>
                    )}

                    <div className={`${styles.fieldGroup} ${styles.fullWidth}`}>
                        <span className={styles.label}><Clock3 size={12} /> Remind In</span>
                        <div className={styles.optionRow}>
                            {REMINDER_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`${styles.chipBtn} ${formData.scheduleType !== 'custom' && String(formData.hours) === option.value ? styles.chipBtnActive : ''}`}
                                    onClick={() => {
                                        updateField('scheduleType', 'quick')
                                        updateField('hours', option.value)
                                    }}
                                >
                                    <CircleDot size={10} /> {option.label}
                                </button>
                            ))}
                            <button
                                type="button"
                                className={`${styles.chipBtn} ${formData.scheduleType === 'custom' ? styles.chipBtnActive : ''}`}
                                onClick={() => updateField('scheduleType', 'custom')}
                            >
                                <Clock3 size={10} /> Pick Date
                            </button>
                        </div>

                        {formData.scheduleType === 'custom' && (
                            <label className={styles.customDateWrap}>
                                <span className={styles.customDateLabel}>Follow-up date & time</span>
                                <input
                                    type="datetime-local"
                                    value={formData.customDateTime || ''}
                                    min={minCustomDateTime}
                                    max={maxCustomDateTime}
                                    onChange={(event) => {
                                        updateField('scheduleType', 'custom')
                                        updateField('customDateTime', event.target.value)
                                    }}
                                />
                            </label>
                        )}
                    </div>

                    <div className={styles.fieldGroup}>
                        <span className={styles.label}>Priority</span>
                        <div className={styles.optionRow}>
                            {PRIORITY_OPTIONS.map((item) => (
                                <button
                                    key={item}
                                    type="button"
                                    className={`${styles.chipBtn} ${String(formData.priority).toLowerCase() === item ? styles.chipBtnActive : ''}`}
                                    onClick={() => updateField('priority', item)}
                                >
                                    {item.charAt(0).toUpperCase() + item.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.fieldGroup}>
                        <span className={styles.label}>Channel</span>
                        <div className={styles.optionRow}>
                            {FOLLOWUP_CHANNELS.map((channel) => (
                                <button
                                    key={channel}
                                    type="button"
                                    className={`${styles.chipBtn} ${String(formData.channel).toLowerCase() === channel ? styles.chipBtnActive : ''}`}
                                    onClick={() => updateField('channel', channel)}
                                >
                                    {channel === 'call' ? <Phone size={10} /> : <MessageSquare size={10} />}
                                    {channel === 'call' ? 'Call' : 'WhatsApp'}
                                </button>
                            ))}
                        </div>
                    </div>

                    <label className={`${styles.fieldGroup} ${styles.fullWidth}`}>
                        <span className={styles.label}><FileText size={12} /> Note</span>
                        <textarea
                            value={formData.note || ''}
                            onChange={(event) => updateField('note', event.target.value)}
                            placeholder="What should recruiter do in this follow-up?"
                            rows={3}
                        />
                    </label>
                </div>

                {errorMessage && (
                    <div className={styles.errorWrap}><AlertCircle size={12} /> {errorMessage}</div>
                )}

                <div className={styles.footerActions}>
                    <button type="button" className={styles.lightBtn} onClick={quickNameFill}>
                        <Sparkles size={12} /> Auto Note
                    </button>
                    <button type="button" className={styles.lightBtn} onClick={resetForm}>
                        Reset
                    </button>

                    <div className={styles.rightActions}>
                        <button type="button" className={styles.cancelBtn} onClick={onClose}>Cancel</button>
                        <button
                            type="button"
                            className={styles.createBtn}
                            onClick={handleSubmit}
                            disabled={isSubmitting || isApiSubmitting}
                        >
                            <Plus size={12} /> {isSubmitting || isApiSubmitting ? 'Creating…' : 'Create Follow-up'}
                        </button>
                    </div>
                </div>
            </div>

        </div>
    )
}

export default AddNewFollowup